/* eslint-disable react/no-array-index-key */
import React from 'react'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import FieldDescription from 'components/ui/FieldDescription'
import FieldError from 'components/ui/FieldError'

export type PossibleValue = string | number | object

export type Option<T extends PossibleValue> = {
  label: React.ReactElement | string
  value: T
}

export type Props<T extends PossibleValue> = {
  label?: string
  description?: string
  name: string
  options: Option<T>[]
  value: T[] | null
  onChange: (value: T) => void
  error?: string | null
  questionId?: number
}

export default function CheckBoxGroup<T extends PossibleValue>({
  label,
  description,
  error,
  options,
  value,
  name,
  onChange,
  questionId
}: Props<T>) {
  return (
    <div
      aria-describedby={`description-${questionId}`}
      aria-labelledby={`label-${questionId}`}
      className="w-full relative"
      role="group"
    >
      {error && (
        <div className="absolute inset-y-0 right-0 top-0 pr-3 pointer-events-none">
          <ExclamationCircleIcon
            aria-hidden="true"
            className="h-5 w-5 text-red-500"
          />
        </div>
      )}
      <p
        className="block text-base font-medium text-gray-700 mb-2"
        id={`label-${questionId}`}
      >
        {label}
      </p>
      <FieldDescription>{description}</FieldDescription>
      <FieldError>{error}</FieldError>
      {options.map((option, index) => (
        <div
          key={`${option.label.toString()}-${option.value.toString()}-${option.value.toString()}-${index}`}
        >
          <label className="inline-flex items-center mt-2">
            <input
              checked={value?.includes(option.value)}
              className="text-orange-500 focus:border-orange-500 focus:ring-orange-500/50 rounded-sm border-gray-300 shadow-xs focus:ring-3 scroll-offset"
              name={name}
              onChange={() => {
                onChange(option.value)
              }}
              type="checkbox"
            />
            <span className="ml-3 text-sm text-gray-700">{option.label}</span>
          </label>
        </div>
      ))}
    </div>
  )
}
